import { Flex } from "antd";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getPointInfoCollectionThunk,
    getStorageInventoryGetThunk,
} from "tm-shared/api";
import {
    CheckoutOrder,
    OrderType,
    ProductOrderItemDetails,
    UserRole,
} from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { getPointStorageId, PointInfo } from "@/domains/point";
import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useIgnoreRemainingsSelector,
    useOfflineModeSelector,
    usePointSelector,
    useProtectedUserRoleSelector,
    useStorageInventorySelector,
} from "@/hooks/redux";
import { useSaveOrder } from "@/pages/Checkout/hooks";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout";
import { DataConfirmModal } from "@/shared/modals";
import { Meta } from "@/shared/seo";
import { BaseButton, PrimaryText, TotalSum } from "@/shared/ui";

import { ProductRow } from "./components/ProductRow";
import {
    StyledODHeader,
    StyledODTable,
    StyledODTableBody,
    StyledODTableHeader,
    StyledODTableHeaderItem,
} from "./Styled";

export const ORDER_TYPES_WITH_DISABLED_CHANGES = [OrderType.P, OrderType.R];

interface OrderDetailsPageProps {
    order: CheckoutOrder;
}

export const OrderDetailsPage: FC<OrderDetailsPageProps> = ({ order }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { handleSaveOrder } = useSaveOrder();

    const orderId = order.orderId;
    const isOrderClosed = order.closed;
    const pointId = order.pointId;

    // --- User / route context ---
    const role = useProtectedUserRoleSelector();

    const currentRoute = useAppSelector((state) => state.manager.currentRoute);

    // --- Offline state ---
    const offlineMode = useOfflineModeSelector();
    const ignoreRemainings = useIgnoreRemainingsSelector(orderId);

    // --- Local UI state ---
    const [validationErrors, setValidationErrors] = useState<
        Record<string, boolean>
    >({});
    const [confirmModal, setConfirmModal] = useState(false);

    // --- Point state ---
    const point = usePointSelector(pointId);
    const storageId = (point && getPointStorageId(point)) ?? "";
    const currentInventoryList = useStorageInventorySelector(storageId);

    const correctPath =
        role === UserRole.Sv
            ? buildRoutePath(ROUTES.SVCheckoutRoute, { routeId: currentRoute })
            : ROUTES.Checkout;

    const [localProducts, setLocalProducts] = useState<
        Record<string, ProductOrderItemDetails>
    >(() => order?.products ?? {});

    const totalOrderPrice = useMemo(() => {
        return Object.values(localProducts).reduce((sum, product) => {
            return sum + product.price * product.count;
        }, 0);
    }, [localProducts]);

    const hasChanges = useMemo(
        () => !isEqual(localProducts, order?.products ?? {}),
        [localProducts, order?.products],
    );

    const validateProductCount = useCallback(
        (barcode: string, count: number) => {
            const maxCount = ignoreRemainings?.includes(barcode)
                ? Number.MAX_SAFE_INTEGER
                : currentInventoryList?.[barcode] || 0;

            const isValid = count <= maxCount;
            setValidationErrors((prev) => ({ ...prev, [barcode]: !isValid }));
            return isValid;
        },
        [currentInventoryList, ignoreRemainings],
    );

    const handleProductCountChange = useCallback(
        (item: ProductOrderItemDetails, newCount: number | null) => {
            const count = newCount ?? 0;

            if (validateProductCount(item.barcode, count)) {
                setLocalProducts((prev) => {
                    const currentItem = prev[item.barcode] ?? item;

                    return {
                        ...prev,
                        [item.barcode]: {
                            ...currentItem,
                            count,
                        },
                    };
                });
            }
        },
        [validateProductCount],
    );

    const handleProductDelete = useCallback((barcode: string) => {
        setLocalProducts((prev) => {
            const updated = { ...prev };
            delete updated[barcode];
            return updated;
        });
    }, []);

    const [onSaveOrder, saveLoading] = useAsyncAction(async () => {
        await handleSaveOrder({
            orderId,
            products: localProducts,
            storageId,
        });

        navigate(
            role === UserRole.Sv
                ? buildRoutePath(ROUTES.SVCheckoutRoute, {
                      routeId: currentRoute ?? "",
                  })
                : buildRoutePath(ROUTES.Checkout),
        );
    });

    useEffect(() => {
        if (offlineMode) return;

        if (!point) {
            dispatch(
                getPointInfoCollectionThunk({
                    ids: [pointId],
                }),
            );
        }

        if (storageId) {
            dispatch(
                getStorageInventoryGetThunk({
                    storageId,
                }),
            );
        }
    }, [dispatch, offlineMode, point, pointId, storageId]);

    const isDisabledType = ORDER_TYPES_WITH_DISABLED_CHANGES.includes(
        order?.orderType,
    );

    const editable = !isOrderClosed && !isDisabledType;

    const visibleProducts = useMemo(
        () => Object.values(localProducts),
        [localProducts],
    );

    const handleAdditionalSale = useCallback(() => {
        if (hasChanges) {
            setConfirmModal(true);
            return;
        }

        navigate(
            buildRoutePath(ROUTES.CheckoutAddProducts, {
                pointId,
                orderId,
                isCheckout: "checkout",
            }),
        );
    }, [hasChanges, pointId, navigate, orderId]);

    if (!order) return <div>Замовлення не знайдено</div>;

    return (
        <>
            <Meta title="Деталі замовлення" />

            <Header
                title="Деталі замовлення"
                path={correctPath}
                needConfirm={hasChanges}
            />

            {totalOrderPrice !== 0 && !isDisabledType && (
                <TotalSum totalSum={totalOrderPrice} />
            )}

            <MainContainer>
                <StyledODHeader>
                    <PointInfo
                        address={point?.market?.building?.address?.name || "-"}
                        fop={point?.market?.contragent?.name || "-"}
                    />
                    <PrimaryText style={{ textWrap: "nowrap" }}>
                        {dayjs().format("DD.MM.YY")}
                    </PrimaryText>
                </StyledODHeader>

                <StyledODTable>
                    <StyledODTableHeader>
                        <StyledODTableHeaderItem>Ціна</StyledODTableHeaderItem>
                        <StyledODTableHeaderItem>К-сть</StyledODTableHeaderItem>
                        <StyledODTableHeaderItem>Сума</StyledODTableHeaderItem>
                    </StyledODTableHeader>
                    <StyledODTableBody>
                        {visibleProducts.map((item) => {
                            const inventoryCount =
                                currentInventoryList?.[item.barcode] ?? 0;

                            const ignoreRemaining = {
                                order: !!ignoreRemainings,
                                product:
                                    ignoreRemainings?.includes(item.barcode) ??
                                    false,
                            };

                            return (
                                <ProductRow
                                    key={item.barcode}
                                    orderId={orderId}
                                    item={item}
                                    isClosed={isOrderClosed}
                                    inventoryCount={inventoryCount}
                                    validationErrors={validationErrors}
                                    onCountChange={handleProductCountChange}
                                    onInputBlur={() =>
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            [item.barcode]: false,
                                        }))
                                    }
                                    onDelete={handleProductDelete}
                                    orderType={order?.orderType}
                                    loading={saveLoading}
                                    ignoreRemaining={ignoreRemaining}
                                />
                            );
                        })}
                    </StyledODTableBody>
                </StyledODTable>

                {editable && (
                    <Flex vertical gap={8}>
                        <BaseButton
                            type="default"
                            onClick={handleAdditionalSale}
                            disabled={saveLoading}
                        >
                            Додатковий продаж
                        </BaseButton>

                        <BaseButton
                            type="primary"
                            onClick={onSaveOrder}
                            disabled={!hasChanges}
                            loading={saveLoading}
                        >
                            Зберегти зміни
                        </BaseButton>
                    </Flex>
                )}
            </MainContainer>

            {confirmModal && hasChanges && (
                <DataConfirmModal
                    onClickBack={() =>
                        navigate(
                            buildRoutePath(ROUTES.CheckoutAddProducts, {
                                pointId,
                                orderId,
                                isCheckout: "checkout",
                            }),
                        )
                    }
                    onClose={() => setConfirmModal(false)}
                    infoText="Введені дані не будуть збережені. Ви впевнені, що хочете перейти на додатковий продаж?"
                    backTextButton="Перейти на додатковий продаж"
                />
            )}
        </>
    );
};
